import { useState, useEffect, useRef, useCallback } from 'react'
import { getRemoteUrl, getData } from './store/request'
import { getLoginToken, requestMyActivities } from './store/store'

const POLL_INTERVAL_MS = 2000
const REFRESH_DEBOUNCE_MS = 3000

export function useConversionStatus() {
  const [files, setFiles] = useState([])
  const [errors, setErrors] = useState([])
  const [loading, setLoading] = useState(false)
  const refreshTimer = useRef(null)

  const filesRef = useRef(files)
  filesRef.current = files

  // Refresh the activities table after a short debounce whenever a file
  // reaches a terminal state, so rapid finishes coalesce into one refresh.
  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    refreshTimer.current = setTimeout(() => {
      refreshTimer.current = null
      requestMyActivities()
    }, REFRESH_DEBOUNCE_MS)
  }, [])

  // Add conversion records from an upload response, deduping by id.
  const register = useCallback((newFiles) => {
    if (!newFiles || !newFiles.length) return
    setFiles((prev) => {
      const known = new Set(prev.map((f) => f.id))
      const added = newFiles
        .filter((f) => !known.has(f.id))
        .map((f) => ({ id: f.id, filename: f.name || f.filename, status: 'queued' }))
      return [...prev, ...added]
    })
  }, [])

  // Single poll loop shared across all registered files. Runs only while there
  // are pending (non-terminal) files, so it stops naturally when work drains.
  useEffect(() => {
    if (files.length === 0) return undefined

    setLoading(true)
    let cancelled = false

    const tick = () => {
      const current = filesRef.current
      if (current.length === 0) return
      const ids = current.map((f) => f.id).join(',')
      const params = {
        method: 'GET',
        headers: { Authorization: `Bearer ${getLoginToken()}` },
      }
      getData(getRemoteUrl(`conversions?ids=${ids}`), params)
        .then((data) => {
          if (cancelled) return
          const byId = {}
          ;(data.files || []).forEach((f) => {
            byId[f.id] = f
          })

          // Compute terminal-file bookkeeping BEFORE touching state, so the
          // setFiles updater below stays pure. State updaters must never carry
          // side effects: React StrictMode (dev) double-invokes updaters, and an
          // impure updater would append duplicated error rows for one failure.
          const current = filesRef.current
          const failures = []
          let terminal = false
          current.forEach((f) => {
            const st = byId[f.id]
            if (!st) return
            if (st.status === 'failed') {
              failures.push({
                filename: f.filename,
                message: st.message || 'Ошибка обработки',
              })
              terminal = true
            } else if (st.status === 'finished') {
              terminal = true
            }
          })
          if (failures.length) {
            setErrors((prevErrors) => [...prevErrors, ...failures])
          }
          if (terminal) scheduleRefresh()

          setFiles((prev) => {
            if (prev.length === 0) return prev
            return prev.filter((f) => {
              const st = byId[f.id]
              if (!st) return true
              // finished | failed — terminal, drop from the pending list.
              if (st.status === 'finished' || st.status === 'failed') return false
              // queued | processing — still pending.
              return true
            })
          })
        })
        .catch(() => {
          // Transient failure; the next tick retries.
        })
    }

    tick()
    const interval = setInterval(tick, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
      setLoading(false)
    }
    // Dependency is intentionally the boolean (not the array) so the loop
    // (re)starts only when the pending set becomes non-empty / empties.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length > 0, scheduleRefresh])

  // Debounced final refresh once the last file finishes.
  const prevLenRef = useRef(files.length)
  useEffect(() => {
    if (files.length === 0 && prevLenRef.current > 0) {
      requestMyActivities()
    }
    prevLenRef.current = files.length
  }, [files.length])

  return { files, errors, register, loading }
}
